import { useEffect, useMemo, useRef, useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { Camera, ImagePlus, Loader2, ZoomIn } from "lucide-react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { updateUserProfile, type UserInfo } from "@/lib/api/auth";
import { useT } from "@/lib/i18n";

const MAX_NICKNAME_LENGTH = 30;
const MAX_AVATAR_SIZE = 5 * 1024 * 1024;
const AVATAR_OUTPUT_SIZE = 512;
const ACCEPTED_AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserInfo;
  onSaved: (user: UserInfo) => void;
}

interface AvatarCropDialogProps {
  open: boolean;
  imageSource: string;
  onOpenChange: (open: boolean) => void;
  onApply: (avatar: CroppedAvatar) => void;
}

interface CroppedAvatar {
  file: File;
  previewUrl: string;
}

function getInitials(user: UserInfo, nickname = user.nickname): string {
  const source = nickname.trim() || user.username || user.email || "U";
  return Array.from(source)[0]?.toUpperCase() || "U";
}

function characterCount(value: string): number {
  return Array.from(value).length;
}

function hasUnsupportedNicknameCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    if (
      codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      codePoint === 0x2028 ||
      codePoint === 0x2029
    ) {
      return true;
    }
    if (character === "\u200c" || character === "\u200d") {
      return false;
    }
    return /\p{Cf}/u.test(character);
  });
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("IMAGE_DECODE_FAILED"));
    image.src = source;
  });
}

async function createCroppedAvatar(imageSource: string, crop: Area): Promise<CroppedAvatar> {
  const image = await loadImage(imageSource);
  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_OUTPUT_SIZE;
  canvas.height = AVATAR_OUTPUT_SIZE;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("CANVAS_UNAVAILABLE");
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    AVATAR_OUTPUT_SIZE,
    AVATAR_OUTPUT_SIZE
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("AVATAR_EXPORT_FAILED"));
          return;
        }

        const outputType = blob.type || "image/webp";
        const extension =
          outputType === "image/png" ? "png" : outputType === "image/jpeg" ? "jpg" : "webp";
        const reader = new FileReader();
        reader.onload = () =>
          resolve({
            file: new File([blob], `avatar.${extension}`, { type: outputType }),
            previewUrl: String(reader.result),
          });
        reader.onerror = () => reject(new Error("AVATAR_EXPORT_FAILED"));
        reader.readAsDataURL(blob);
      },
      "image/webp",
      0.9
    );
  });
}

function AvatarCropDialog({ open, imageSource, onOpenChange, onApply }: AvatarCropDialogProps) {
  const t = useT();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!croppedAreaPixels || isProcessing) return;

    setIsProcessing(true);
    setError(null);
    try {
      const avatar = await createCroppedAvatar(imageSource, croppedAreaPixels);
      onApply(avatar);
    } catch {
      setError(
        t("Failed to crop this image. Please try another one.", "图片裁剪失败，请更换图片后重试。")
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !isProcessing && onOpenChange(nextOpen)}>
      <DialogContent className="landing-v2 w-[calc(100vw-2rem)] max-w-[520px] gap-6 rounded-3xl border-border bg-background p-5 sm:p-7">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-xl text-foreground">
            {t("Crop avatar", "裁剪头像")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Drag the image and adjust the zoom to frame your avatar.",
              "拖动图片并调整缩放，选择最合适的头像区域。"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-[min(58vw,320px)] min-h-[240px] overflow-hidden rounded-2xl border border-white/10 bg-black">
          <Cropper
            image={imageSource}
            crop={crop}
            zoom={zoom}
            minZoom={1}
            maxZoom={3}
            aspect={1}
            cropShape="round"
            showGrid={false}
            objectFit="cover"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.01}
              aria-label={t("Avatar zoom", "头像缩放")}
              onValueChange={([value]) => setZoom(value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="flex-row justify-end gap-3 space-x-0">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={isProcessing}
            onClick={() => onOpenChange(false)}
          >
            {t("Cancel", "取消")}
          </Button>
          <Button
            type="button"
            className="min-w-24 rounded-xl bg-foreground text-background hover:bg-foreground/90"
            disabled={!croppedAreaPixels || isProcessing}
            onClick={() => void handleApply()}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-label={t("Processing", "处理中")} />
            ) : (
              t("Apply", "应用")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProfileDialog({ open, onOpenChange, user, onSaved }: ProfileDialogProps) {
  const t = useT();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [nickname, setNickname] = useState(user.nickname || "");
  const [avatar, setAvatar] = useState(user.avatar || "");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNickname(user.nickname || "");
    setAvatar(user.avatar || "");
    setAvatarFile(null);
    setAvatarError(null);
    setSaveError(null);
    setIsSaving(false);
    setDiscardDialogOpen(false);
  }, [open, user]);

  useEffect(() => {
    return () => {
      if (cropSource) URL.revokeObjectURL(cropSource);
    };
  }, [cropSource]);

  const trimmedNickname = nickname.trim();
  const nicknameLength = characterCount(trimmedNickname);
  const nicknameError = useMemo(() => {
    if (!trimmedNickname) return t("Nickname is required.", "请输入昵称。");
    if (nicknameLength > MAX_NICKNAME_LENGTH) {
      return t(
        `Nickname must be ${MAX_NICKNAME_LENGTH} characters or fewer.`,
        `昵称不能超过 ${MAX_NICKNAME_LENGTH} 个字符。`
      );
    }
    if (hasUnsupportedNicknameCharacter(trimmedNickname)) {
      return t(
        "Nickname cannot contain line breaks, control characters, or invisible formatting characters.",
        "昵称不能包含换行、控制字符或不可见格式字符。"
      );
    }
    return null;
  }, [nicknameLength, t, trimmedNickname]);
  const nicknameChanged = trimmedNickname !== (user.nickname || "").trim();
  const visibleNicknameError = nicknameChanged ? nicknameError : null;
  const isDirty = nicknameChanged || Boolean(avatarFile);
  const canSave = isDirty && !visibleNicknameError && !avatarError;

  const requestClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardDialogOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    requestClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ACCEPTED_AVATAR_TYPES.has(file.type)) {
      setAvatarError(t("Use a JPG, PNG, or WebP image.", "请选择 JPG、PNG 或 WebP 图片。"));
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      setAvatarError(t("Image size must be 5 MB or less.", "图片大小不能超过 5 MB。"));
      return;
    }

    const source = URL.createObjectURL(file);
    try {
      await loadImage(source);
      setAvatarError(null);
      setSaveError(null);
      setCropSource(source);
    } catch {
      URL.revokeObjectURL(source);
      setAvatarError(
        t("This image cannot be opened. Please try another one.", "图片无法读取，请更换后重试。")
      );
    }
  };

  const handleCropClose = () => setCropSource(null);

  const handleCropApply = (nextAvatar: CroppedAvatar) => {
    setAvatar(nextAvatar.previewUrl);
    setAvatarFile(nextAvatar.file);
    setAvatarError(null);
    setSaveError(null);
    setCropSource(null);
  };

  const handleSave = async () => {
    if (!canSave || isSaving) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const result = await updateUserProfile({
        nickname: nicknameChanged ? trimmedNickname : undefined,
        avatar: avatarFile ?? undefined,
      });
      const nextUser: UserInfo = {
        ...user,
        nickname: result.nickname,
        avatar: result.avatar,
      };
      onSaved(nextUser);
      onOpenChange(false);
      toast.success(t("Profile updated", "个人资料已更新"));
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : t("Failed to update profile. Please try again.", "个人资料修改失败，请稍后重试。")
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="landing-v2 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[520px] overflow-y-auto rounded-3xl border-border bg-background p-6 sm:p-8">
          <DialogHeader className="pr-8 text-left">
            <DialogTitle className="text-2xl text-foreground">
              {t("Personal profile", "个人资料")}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4 py-3">
            <div className="relative">
              <Avatar className="h-24 w-24 border border-white/15 bg-muted ring-4 ring-brand/10 shadow-[0_0_40px_-12px_hsl(var(--brand)/0.8)] sm:h-28 sm:w-28">
                {avatar && <AvatarImage src={avatar} alt={trimmedNickname || user.username} />}
                <AvatarFallback className="bg-muted text-3xl font-medium text-foreground">
                  {getInitials(user, trimmedNickname)}
                </AvatarFallback>
              </Avatar>
              <button
                type="button"
                className="absolute bottom-0 right-0 grid h-9 w-9 place-items-center rounded-full border-4 border-background bg-brand text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={t("Change avatar", "更换头像")}
                disabled={isSaving}
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => void handleFileChange(event)}
            />

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="gap-2 rounded-xl"
                disabled={isSaving}
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus className="h-4 w-4" aria-hidden />
                {t("Change avatar", "更换头像")}
              </Button>
            </div>
            <p className="text-center text-xs text-muted-foreground">
              {t("JPG, PNG or WebP, up to 5 MB", "支持 JPG、PNG、WebP，最大 5 MB")}
            </p>
            {avatarError && <p className="text-center text-sm text-destructive">{avatarError}</p>}
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <label htmlFor="profile-nickname" className="text-sm font-medium text-foreground">
                  {t("Nickname", "昵称")}
                </label>
                <span
                  className={`text-xs ${nicknameLength > MAX_NICKNAME_LENGTH ? "text-destructive" : "text-muted-foreground"}`}
                >
                  {nicknameLength}/{MAX_NICKNAME_LENGTH}
                </span>
              </div>
              <Input
                id="profile-nickname"
                value={nickname}
                autoComplete="nickname"
                aria-invalid={Boolean(visibleNicknameError)}
                aria-describedby={visibleNicknameError ? "profile-nickname-error" : undefined}
                className="h-12 rounded-2xl border-0 bg-muted/50 px-4 focus-visible:ring-1 focus-visible:ring-brand"
                disabled={isSaving}
                onChange={(event) => {
                  setNickname(event.target.value);
                  setSaveError(null);
                }}
              />
              {visibleNicknameError && (
                <p id="profile-nickname-error" className="text-sm text-destructive">
                  {visibleNicknameError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <label htmlFor="profile-email" className="text-sm font-medium text-foreground">
                  {t("Login email", "登录邮箱")}
                </label>
                <span className="text-xs text-muted-foreground">{t("Read only", "不可修改")}</span>
              </div>
              <Input
                id="profile-email"
                value={user.email}
                readOnly
                className="h-12 cursor-not-allowed rounded-2xl border-0 bg-muted/30 px-4 text-muted-foreground"
              />
            </div>
          </div>

          {saveError && (
            <div
              role="alert"
              className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              {saveError}
            </div>
          )}

          <DialogFooter className="mt-2 flex-row justify-end gap-3 space-x-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={isSaving}
              onClick={requestClose}
            >
              {t("Cancel", "取消")}
            </Button>
            <Button
              type="button"
              className="rounded-xl bg-foreground px-5 text-background hover:bg-foreground/90"
              disabled={!canSave || isSaving}
              onClick={() => void handleSave()}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {t("Saving...", "保存中...")}
                </span>
              ) : (
                t("Save changes", "保存更改")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cropSource && (
        <AvatarCropDialog
          key={cropSource}
          open
          imageSource={cropSource}
          onOpenChange={(nextOpen) => !nextOpen && handleCropClose()}
          onApply={handleCropApply}
        />
      )}

      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent className="landing-v2 w-[calc(100vw-2rem)] max-w-md rounded-3xl border-border bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Discard changes?", "放弃更改？")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "Your unsaved profile changes will be lost.",
                "尚未保存的头像和昵称修改将会丢失。"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">
              {t("Keep editing", "继续编辑")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => onOpenChange(false)}
            >
              {t("Discard", "放弃更改")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
